import React, { MouseEventHandler } from 'react'
import classNames from 'classnames'
import { Avatar } from 'transfix-ui/components/Avatar'
import NavigationItemMenu from 'transfix-ui/components/NavigationItemMenu'
import NotificationBadge from 'transfix-ui/components/NotificationBadge'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { INavRoute } from 'types/routes'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useHover } from '../hooks'

const NAVBAR_HEIGHT_PX = 48

interface INavigationItemAvatarProps {
  className?: string
  route: INavRoute
  /**
   * @default false
   */
  isOpen?: boolean
}

const useNavigationItemAvatarStyles = makeStyles(theme => ({
  root: {
    display: 'none',
    position: 'relative',
    height: NAVBAR_HEIGHT_PX,
    backgroundColor: brandColors.coolGray8,
    color: brandColors.coolGray4,
    alignItems: 'center',

    /**
     * On desktop and above, show this item
     */
    [theme.breakpoints.up('lg')]: {
      display: 'inline-flex',
    },
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    color: brandColors.coolGray4,

    '&:after': {
      position: 'absolute',
      backgroundColor: brandColors.coolGray8,
      height: 2,
      width: '100%',
      bottom: 0,
      left: 0,
      right: 0,
      content: '""',
    },

    '&:hover': {
      cursor: 'pointer',
      color: brandColors.white,
      textDecoration: 'none',

      '& .avatar': {
        cursor: 'pointer',
      },

      '&:after': {
        backgroundColor: brandColors.white,
      },
    },
  },
  open: {
    '&:after': {
      backgroundColor: brandColors.white,
    },
  },
}))

const NavigationItemAvatar = ({
  className: classNameProp,
  isOpen: isOpenProp = false,
  route: routeProp,
}: INavigationItemAvatarProps) => {
  const { location, AvatarProps = {}, Menu = NavigationItemMenu, MenuProps, notificationCount } =
    routeProp || {}
  const { isDesktop, isMobile } = useBreakpointContext()
  const { isHover, onMouseEnter, onMouseLeave } = useHover()
  const classes = useNavigationItemAvatarStyles()
  const isOpen = isDesktop ? isHover : isOpenProp
  const className = classNames(classes.root, classNameProp)
  const linkClassName = classNames(classes.link, { [classes.open]: isOpen })

  const clickHandler: MouseEventHandler<HTMLAnchorElement> = e => {
    e.preventDefault()
  }

  return (
    <li
      data-test='NavigationItemAvatar'
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <a href={location} className={linkClassName} onClick={clickHandler}>
        <NotificationBadge notificationCount={notificationCount}>
          <Avatar name='Joe John' size='xsmall' hideShadow hideTooltip {...AvatarProps} />
        </NotificationBadge>
      </a>
      {MenuProps && <Menu {...MenuProps} isOpen={isOpen} isMobile={isMobile} />}
    </li>
  )
}

export default NavigationItemAvatar
